import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, Checkbox, Autocomplete, Chip, TextField, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import { procedureSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateProcedureDetailsMutation, useGetSpecialitiesQuery, useGetProcedureDetailQuery } from "../../services/api";
import style from "./procedure.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof procedureSchema.__outputType;
type SpecialityOption = {
  id: number;
  label: string;
  value: string;
};

/**
 * Edit Procedure page for updating an existing procedure.
 *
 * Notes:
 * - Loads procedure details and speciality options before reset.
 * - Validation is enforced via `procedureSchema`.
 */
const EditProcedure = () => {
  const [updateProcedure] = useUpdateProcedureDetailsMutation();
  const { id } = useParams();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: specialityData, isLoading: isSpecialityLoading } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });

  const specialities = useMemo<SpecialityOption[]>(
    () =>
      specialityData?.items?.map((speciality: Speciality) => {
        return {
          id: speciality?.id,
          label: speciality?.name,
          value: speciality?.name,
        };
      }) ?? [],
    [specialityData]
  );

  const {
    data: procedureDetails,
    isLoading,
    isFetching,
  } = useGetProcedureDetailQuery(id ?? "");

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingProcedure, setIsSavingProcedure] = useState(false);
  const [specialityDetails, setSpecialityDetails] = useState<Speciality[]>([]);

  useEffect(() => {
    if (specialityData && procedureDetails) {
      // Map stored ids to full objects; filter missing items to keep form values consistent.
      const mappedSpecialities = procedureDetails?.speciality_ids
        ?.map((specialityId) =>
          specialityData?.items?.find(
            (speciality: Speciality) => speciality.id === specialityId
          )
        )
        .filter((speciality): speciality is Speciality => Boolean(speciality));
      setSpecialityDetails(mappedSpecialities ?? []);
    }
  }, [specialityData, procedureDetails]);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(procedureSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    reset
  } = useForm<FormState>({
    resolver: yupResolver(procedureSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!isEmpty(procedureDetails)) {
      // Reset form state from server data to avoid stale edits when navigating between records.
      reset({
        name: procedureDetails?.name,
        speciality_ids: specialityDetails?.map((speciality: Speciality) => {
          return {
            id: speciality?.id,
            label: speciality?.name,
            value: speciality?.name,
          };
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialityDetails, procedureDetails]);

  /**
   * Submits the updated procedure payload.
   *
   * Args:
   *   values (FormState): Form values for the procedure update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; blocks submit when `id` is invalid.
   * - TODO: Confirm expected behavior when `id` is missing or invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Block updates if route id is missing/invalid to avoid unintended mutations.
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update procedure. Please reload and try again.",
        });
        return;
      }
      setIsSavingProcedure(true);
      const { name, speciality_ids } = values;
      const payload: EditProcedurePayload = {
        id: parsedId,
        name: name,
        speciality_ids: speciality_ids?.map((speciality) => speciality.id) ?? [],
      };

      await updateProcedure(payload).unwrap();
      setIsSavingProcedure(false);
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingProcedure(false);
    }
  };
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading || isSpecialityLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Procedure Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Biopsy"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
            <Box className={style.row} sx={{ flexDirection: "column" }}>
              <Typography
                className={style.inputLabel}
                sx={{ marginBottom: -2, display: "flex" }}
              >
                Specialities Mapping
                <Typography sx={{ color: "red", marginLeft: 0.7 }}>
                  {" *"}
                </Typography>
              </Typography>
              <Controller
                control={control}
                name={"speciality_ids"}
                rules={{ required: true }}
                render={({ field: { onChange, value }, ...props }) => (
                  <Autocomplete<SpecialityOption, true, true, false>
                    {...props}
                    sx={{
                      width: "100%",
                    }}
                    multiple
                    disablePortal
                    disableCloseOnSelect
                    id="tags-outlined"
                    options={specialities}
                    disableClearable={true}
                    onChange={(e, data) => onChange(data)}
                    getOptionLabel={(option: SpecialityOption) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={value ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={
                          isEmpty(getValues("speciality_ids"))
                            ? "Select specialities"
                            : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        error={!!errors.speciality_ids}
                        helperText={
                          errors.speciality_ids
                            ? "Speciality is required"
                            : ""
                        }
                      />
                    )}
                    renderTags={(
                      value: readonly SpecialityOption[],
                      getTagProps
                    ) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.label}
                      </li>
                    )}
                  />
                )}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingProcedure}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditProcedure;
